import { backendApiService } from './backendApiService';

export interface Schema {
  id: string;
  url: string;
  pinecone_doc_id: string | null;
  priority: string | null;
  content_type: string | null;
  schema_json: any;
  schema_version: number;
  completeness_score: number | null;
  status: string;
  deployed_at: string | null;
  deployed_by: string | null;
  created_at: string;
  updated_at: string;
  manual_edits?: Record<string, { edited_at: string; edited_by: string }>;
  faq_opportunities?: any[];
  content_gaps?: any[];
  faq_count?: number;
  content_gaps_count?: number;
  is_skipped?: boolean;
  reason?: string;
  detected_type?: string;
  url_id?: string;
  paa_questions?: PAAQuestion[];
  paa_stats?: {
    total: number;
    answered: number;
    from_schema: number;
    from_content: number;
    needs_research: number;
  };
  verification_status?: Record<string, any>;
  faq_filtering_savings?: {
    last_run: string;
    total_questions: number;
    venue_specific: number;
    general_tourism: number;
    percentage_filtered: number;
    estimated_cost_savings: string;
  };
}

export interface PAAQuestion {
  question_id: string;
  tree_id: string;
  question_text: string;
  answer_text?: string;
  answer_source?: 'schema' | 'content' | 'research';
  answer_confidence?: number;
  answered_at?: string;
  depth_level: number;
  position_in_level: number;
}

export interface SchemaListResponse {
  success: boolean;
  data: Schema[];
  total: number;
  limit: number;
  offset: number;
}

export interface QueueStatusResponse {
  success: boolean;
  queue: Array<{
    priority: string | null;
    status: string;
    count: number;
  }>;
  recentCompletions: any[];
}

class SchemaApiService {
  /**
   * List all generated schemas with filters
   */
  async listSchemas(params?: {
    status?: string;
    priority?: string;
    limit?: number;
    offset?: number;
    search?: string;
  }): Promise<SchemaListResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status) queryParams.append('status', params.status);
      if (params?.priority) queryParams.append('priority', params.priority);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.offset) queryParams.append('offset', params.offset.toString());
      if (params?.search) queryParams.append('search', params.search);

      const response = await backendApiService.get<SchemaListResponse>(
        `/api/schema/list?${queryParams.toString()}`
      );
      return response;
    } catch (error) {
      console.error('Error listing schemas:', error);
      throw error;
    }
  }

  /**
   * Get a specific schema with all details
   */
  async getSchema(id: string): Promise<{ success: boolean; data: Schema }> {
    try {
      const response = await backendApiService.get<{ success: boolean; data: Schema }>(
        `/api/schema/${id}`
      );
      return response;
    } catch (error) {
      console.error('Error getting schema:', error);
      throw error;
    }
  }

  /**
   * Generate schema for a single URL
   */
  async generateSingleSchema(url: string): Promise<{ success: boolean; data: Schema }> {
    try {
      const response = await backendApiService.post<{ success: boolean; data: Schema }>(
        '/api/schema/generate-single',
        { url }
      );
      return response;
    } catch (error) {
      console.error('Error generating single schema:', error);
      throw error;
    }
  }

  /**
   * Start batch processing for multiple URLs
   */
  async startBatchProcessing(params: {
    priority?: string;
    limit?: number;
    websiteId?: string;
  }): Promise<{ 
    success: boolean; 
    message: string; 
    queued: number; 
    error?: string;
    demoSchema?: any;
    batchId?: string;
    status?: string;
    expiresAt?: string;
    costSavings?: string;
    estimatedCompletion?: string;
  }> {
    try {
      const response = await backendApiService.post<{ 
        success: boolean; 
        message: string; 
        queued: number;
        error?: string;
        demoSchema?: any;
        batchId?: string;
        status?: string;
        expiresAt?: string;
        costSavings?: string;
        estimatedCompletion?: string;
      }>(
        '/api/schema/generate-batch',
        params
      );
      return response;
    } catch (error) {
      console.error('Error starting batch processing:', error);
      throw error;
    }
  }

  /**
   * Trigger Phase 2 research for a schema
   */
  async triggerResearch(schemaId: string, immediate: boolean = false): Promise<{ 
    success: boolean; 
    message?: string;
    fieldsUpdated?: number;
    completeness?: number;
    sources?: any[];
    queueId?: string;
  }> {
    try {
      const response = await backendApiService.post<{ 
        success: boolean; 
        message?: string;
        fieldsUpdated?: number;
        completeness?: number;
        sources?: any[];
        queueId?: string;
      }>(
        `/api/research/${schemaId}`,
        { immediate }
      );
      return response;
    } catch (error) {
      console.error('Error triggering research:', error);
      throw error;
    }
  }

  /**
   * Get research status for a schema
   */
  async getResearchStatus(schemaId: string): Promise<{
    success: boolean;
    status: string;
    queueItem: any;
    sources: any[];
  }> {
    try {
      const response = await backendApiService.get<{
        success: boolean;
        status: string;
        queueItem: any;
        sources: any[];
      }>(`/api/research/${schemaId}/status`);
      return response;
    } catch (error) {
      console.error('Error getting research status:', error);
      throw error;
    }
  }

  /**
   * Get research sources for a schema
   */
  async getResearchSources(schemaId: string): Promise<{
    success: boolean;
    sources: any[];
  }> {
    try {
      const response = await backendApiService.get<{
        success: boolean;
        sources: any[];
      }>(`/api/research/sources/${schemaId}`);
      return response;
    } catch (error) {
      console.error('Error getting research sources:', error);
      throw error;
    }
  }

  /**
   * Mark schema as copied
   */
  async markCopied(schemaId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await backendApiService.post<{ success: boolean; message: string }>(
        '/api/schema/mark-copied',
        { schemaId }
      );
      return response;
    } catch (error) {
      console.error('Error marking schema as copied:', error);
      throw error;
    }
  }

  /**
   * Update schema status
   */
  async updateStatus(
    id: string, 
    status: 'draft' | 'approved' | 'deployed' | 'archived',
    deployedBy?: string
  ): Promise<{ success: boolean; data: Schema }> {
    try {
      const response = await backendApiService.put<{ success: boolean; data: Schema }>(
        `/api/schema/${id}/status`,
        { status, deployedBy }
      );
      return response;
    } catch (error) {
      console.error('Error updating schema status:', error);
      throw error;
    }
  }

  /**
   * Get processing queue status
   */
  async getQueueStatus(): Promise<QueueStatusResponse> {
    try {
      const response = await backendApiService.get<QueueStatusResponse>(
        '/api/schema/queue/status'
      );
      return response;
    } catch (error) {
      console.error('Error getting queue status:', error);
      throw error;
    }
  }

  /**
   * Check batch status
   */
  async checkBatchStatus(batchId: string): Promise<{
    success: boolean;
    batch: {
      id: string;
      status: string;
      requestCounts: any;
      endedAt: string | null;
      resultsUrl: string | null;
    }
  }> {
    try {
      const response = await backendApiService.get<{
        success: boolean;
        batch: any;
      }>(`/api/schema/batch/${batchId}`);
      return response;
    } catch (error) {
      console.error('Error checking batch status:', error);
      throw error;
    }
  }

  /**
   * Process batch results
   */
  async processBatchResults(batchId: string): Promise<{
    success: boolean;
    message: string;
    results: any;
  }> {
    try {
      const response = await backendApiService.post<{
        success: boolean;
        message: string;
        results: any;
      }>(`/api/schema/batch/${batchId}/process-results`, {});
      return response;
    } catch (error) {
      console.error('Error processing batch results:', error);
      throw error;
    }
  }

  /**
   * Copy schema to clipboard
   */
  async copySchemaToClipboard(schema: Schema, removePricing: boolean = false): Promise<boolean> {
    try {
      // Import the cleanup function
      const { cleanSchemaForDeployment } = await import('../utils/schemaCleanup');
      
      // Clean the schema for deployment (remove underscore fields)
      let cleanedSchema = cleanSchemaForDeployment(schema.schema_json);
      
      // Remove pricing fields if requested
      if (removePricing) {
        const { offers, priceRange, ...schemaWithoutPricing } = cleanedSchema;
        cleanedSchema = schemaWithoutPricing;
      }
      
      // Format the schema JSON nicely
      const formattedSchema = JSON.stringify(cleanedSchema, null, 2);
      
      // Copy to clipboard
      await navigator.clipboard.writeText(formattedSchema);
      
      // Mark as copied in the backend
      await this.markCopied(schema.id);
      
      return true;
    } catch (error) {
      console.error('Error copying schema to clipboard:', error);
      return false;
    }
  }

  /**
   * Mark FAQ as not relevant
   */
  async markFAQAsNotRelevant(questionId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await backendApiService.put<{ success: boolean; message: string }>(
        `/api/paa/mark-not-relevant/${questionId}`,
        { not_relevant: true }
      );
      return response;
    } catch (error) {
      console.error('Error marking FAQ as not relevant:', error);
      throw error;
    }
  }

  /**
   * Update schema JSON
   */
  async updateSchema(schemaId: string, schemaJson: any, editedFields?: string[]): Promise<{ success: boolean; data: Schema }> {
    try {
      const response = await backendApiService.put<{ success: boolean; data: Schema }>(
        `/api/schema/${schemaId}/update`,
        { 
          schema_json: schemaJson,
          edited_fields: editedFields 
        }
      );
      return response;
    } catch (error) {
      console.error('Error updating schema:', error);
      throw error;
    }
  }

  /**
   * Verify schema fields
   */
  async verifySchema(schemaId: string): Promise<{ success: boolean; verificationStatus: Record<string, any> }> {
    try {
      const response = await backendApiService.post<{ success: boolean; verificationStatus: Record<string, any> }>(
        `/api/schema/${schemaId}/verify`,
        {}
      );
      return response;
    } catch (error) {
      console.error('Error verifying schema:', error);
      throw error;
    }
  }
}

export const schemaApiService = new SchemaApiService();